import { useEffect, useState } from 'react';
import { fetchPrintLanes } from '../services/printApi';
import type { PrinterTarget } from '../services/printerDefaults';

/**
 * The label printers the Print page may address.
 *
 * Reads `GET /api/print/lanes`, not the admin `GET /api/printers` — the picker
 * is used by whoever is standing at the counter, so it must not be admin-gated,
 * and a client only ever needs the lane it addresses (`siteId` + `role`), never
 * the printer's host/port.
 *
 * Only `brother-raster` lanes are offered. Every document on this page is a
 * 90mm Brother label, and the on-site agent checks `printer.engine ==
 * payload.engine` before dispatching — addressing a receipt or PDF lane would
 * not print a smaller label, it would raise a DispatchError, abandon the
 * message and eventually dead-letter it with nothing on screen to say so. So a
 * lane that cannot render this page's documents is not a choice.
 */
export type LabelPrinterOption = PrinterTarget;

const LABEL_ENGINE = 'brother-raster';

export function usePrintLabelPrinters(): {
  printers: LabelPrinterOption[];
  isLoading: boolean;
  error: string | null;
} {
  const [printers, setPrinters] = useState<LabelPrinterOption[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    setIsLoading(true);
    fetchPrintLanes(controller.signal)
      .then((lanes) => {
        if (controller.signal.aborted) return;
        setPrinters(
          lanes
            .filter((lane) => lane.engine === LABEL_ENGINE)
            .map((lane) => ({ siteId: lane.siteId, role: lane.role })),
        );
        setError(null);
      })
      .catch((err) => {
        if (controller.signal.aborted) return;
        setPrinters([]);
        setError(err instanceof Error ? err.message : 'Failed to load printers');
      })
      .finally(() => {
        if (!controller.signal.aborted) setIsLoading(false);
      });
    return () => controller.abort();
  }, []);

  return { printers, isLoading, error };
}
